/**
 * Prime Magic Strategy — faithful port of MT4 MT4_Prime_Magic.mq4
 *
 * Uses an inline ATR-based SuperTrend system:
 *   - Period = 7, Multiplier = 4.0 (configurable)
 *   - Upper = midPoint + multiplier × ATR
 *   - Lower = midPoint − multiplier × ATR
 *   - Direction: close > prev upper → LONG, close < prev lower → SHORT
 *   - Bands trail (never move against direction)
 *
 * Signal on direction flip:
 *   BUY  → Entry = Close, SL = trailing lower band (LongTS),
 *           Risk = Close − prev Low, TP1 = Entry + 1.5×Risk, TP2 = Entry + 3.5×Risk
 *   SELL → Entry = Close, SL = trailing upper band (ShortTS),
 *           Risk = prev High − Close, TP1 = Entry − 1.5×Risk, TP2 = Entry − 3.5×Risk
 *
 * MT4 features ported:
 *   - Buy/Sell arrow markers (codes 233/234)
 *   - Custom colored candles: Green (#008000, long), Red (#FF0000, short), Gray (#808080, neutral)
 *   - Trailing stop = SuperTrend band value
 *   - Target/SL hit detection with ✓ marks
 *   - Live P/L, booked P/L, hit status tracking
 *   - Dashboard metadata for overlay component
 *   - Entry/SL/TP1/TP2 level lines
 *
 * Category: Strategies | Pane: Overlay
 */
import { IndicatorDefinition, PlotData, IndicatorMarker, IndicatorPriceLine } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { inferTickSize, roundToTick } from "./price-utils";

// ─── Constants ───────────────────────────────────────────────────
const DIR_NONE = 0;
const DIR_LONG = 1;
const DIR_SHORT = -1;
type Direction = typeof DIR_NONE | typeof DIR_LONG | typeof DIR_SHORT;

// ─── ATR computation (MT4 iATR: Wilder smoothing) ────────────────
function computeATR(candles: CandleData[], period: number): number[] {
  const atr: number[] = new Array(candles.length).fill(0);
  if (candles.length < 2) return atr;

  const tr: number[] = [candles[0].high - candles[0].low];
  for (let i = 1; i < candles.length; i++) {
    const h = candles[i].high;
    const l = candles[i].low;
    const pc = candles[i - 1].close;
    tr.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
  }

  let sum = 0;
  for (let i = 0; i < Math.min(period, tr.length); i++) sum += tr[i];
  atr[period - 1] = sum / period;
  for (let i = period; i < tr.length; i++) {
    atr[i] = (atr[i - 1] * (period - 1) + tr[i]) / period;
  }
  return atr;
}

// ─── Dashboard data interface ────────────────────────────────────
export interface PrimeMagicDashboard {
  signal: "BUY" | "SELL" | null;
  trend: "LONG" | "SHORT" | "NEUTRAL";
  entry: number | null;
  stopLoss: number | null;
  trailingStop: number | null;
  target1: number | null;
  target2: number | null;
  currentPrice: number;
  livePL: number;
  bookedPL: number;
  status: "Active" | "Target1 Hit" | "Target2 Hit" | "Stop Loss Hit" | "Trailing SL Hit";
  tp1Hit: boolean;
  tp2Hit: boolean;
  slHit: boolean;
  tslHit: boolean;
  candleState: "long" | "short" | "neutral";
}

export const primeMagicIndicator: IndicatorDefinition = {
  id: "prime_magic",
  name: "Prime Magic",
  shortName: "PMG",
  category: "strategies",
  pane: "overlay",
  params: [
    { key: "alertEnabled", label: "Alert Notification", type: "boolean", default: true },
    { key: "period", label: "Period", type: "number", default: 7, hidden: true },
    { key: "multiplier", label: "Multiplier", type: "number", default: 4.0, hidden: true },
    { key: "displayLevels", label: "Display Lines", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
    { key: "lineWidth", label: "Line Width", type: "number", default: 1, min: 1, max: 4, step: 1 },
    { key: "showBackground", label: "Candle Color", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
    { key: "showTrailingStop", label: "Show Trailing SL", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
  ],
  plots: [
    { key: "entry", label: "Entry", color: "#1565C0", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "stopLoss", label: "Stop Loss", color: "#FF0000", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target1", label: "Target 1", color: "#006400", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target2", label: "Target 2", color: "#006400", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const period = params.period ?? 7;
    const multiplier = params.multiplier ?? 4.0;
    const displayLevels = params.displayLevels ?? "yes";
    const showTrailingStop = params.showTrailingStop ?? "yes";
    const len = candles.length;
    const minBars = period + 2;

    const emptyResult: PlotData[] = [
      { key: "entry", data: [] },
      { key: "stopLoss", data: [] },
      { key: "target1", data: [] },
      { key: "target2", data: [] },
    ];

    if (len < minBars) return emptyResult;

    const tick = inferTickSize(candles);
    const lw = Math.max(1, Math.min(4, Math.round(params.lineWidth ?? 1)));
    const showBackground = params.showBackground ?? "yes";
    // ════════════════════════════════════════════════════════════
    // 1. Inline SuperTrend (MT4 PM_PERIOD=7, PM_MULTIPLIER=4.0)
    // ════════════════════════════════════════════════════════════
    const atr = computeATR(candles, period);

    const upperRaw: number[] = new Array(len).fill(0);
    const lowerRaw: number[] = new Array(len).fill(0);
    const upper: number[] = new Array(len).fill(0);
    const lower: number[] = new Array(len).fill(0);
    const direction: Direction[] = new Array(len).fill(DIR_LONG);
    const longTS: number[] = new Array(len).fill(0);
    const shortTS: number[] = new Array(len).fill(0);

    for (let i = 0; i < len; i++) {
      const midPoint = (candles[i].high + candles[i].low) / 2.0;
      upperRaw[i] = midPoint + multiplier * atr[i];
      lowerRaw[i] = midPoint - multiplier * atr[i];
    }

    upper[0] = upperRaw[0];
    lower[0] = lowerRaw[0];
    direction[0] = DIR_LONG;

    for (let i = 1; i < len; i++) {
      const close = candles[i].close;

      if (close > upper[i - 1]) {
        direction[i] = DIR_LONG;
      } else if (close < lower[i - 1]) {
        direction[i] = DIR_SHORT;
      } else {
        direction[i] = direction[i - 1];
      }

      lower[i] = lowerRaw[i];
      if (direction[i] === DIR_LONG && lower[i] < lower[i - 1]) {
        lower[i] = lower[i - 1];
      }

      upper[i] = upperRaw[i];
      if (direction[i] === DIR_SHORT && upper[i] > upper[i - 1]) {
        upper[i] = upper[i - 1];
      }

      const prevDir = direction[i - 1];
      if (direction[i] === DIR_SHORT && prevDir === DIR_LONG) {
        upper[i] = upperRaw[i];
      }
      if (direction[i] === DIR_LONG && prevDir === DIR_SHORT) {
        lower[i] = lowerRaw[i];
      }

      if (direction[i] === DIR_LONG) {
        longTS[i] = lower[i];
      } else {
        shortTS[i] = upper[i];
      }
    }

    // ════════════════════════════════════════════════════════════
    // 2. Signal Generation — direction flip detection
    // ════════════════════════════════════════════════════════════
    const markers: IndicatorMarker[] = [];
    const SIGNAL: Direction[] = new Array(len).fill(DIR_NONE);
    const ENTRY: number[] = new Array(len).fill(0);
    const SL: number[] = new Array(len).fill(0);
    const TSL: number[] = new Array(len).fill(0);
    const TP1: number[] = new Array(len).fill(0);
    const TP2: number[] = new Array(len).fill(0);
    const candleColorState: ("long" | "short" | "neutral")[] = new Array(len).fill("neutral");
    const arrowMarkers: { time: number; direction: "long" | "short"; price: number }[] = [];

    let lastSignalDir: Direction = DIR_NONE;
    let lastSignalBar = -1;
    const lastClosedBar = len - 2;

    for (let i = 1; i <= lastClosedBar; i++) {
      const curDir = direction[i];
      const prevDir = direction[i - 1];

      const isBuySignal = curDir === DIR_LONG && prevDir === DIR_SHORT;
      const isSellSignal = curDir === DIR_SHORT && prevDir === DIR_LONG;

      if (isBuySignal) {
        SIGNAL[i] = DIR_LONG;
        ENTRY[i] = roundToTick(candles[i].close, tick);
        SL[i] = roundToTick(longTS[i], tick);
        TSL[i] = SL[i];
        const risk = Math.max(candles[i].close - candles[i - 1].low, 0.01);
        TP1[i] = roundToTick(ENTRY[i] + 1.5 * risk, tick);
        TP2[i] = roundToTick(ENTRY[i] + 3.5 * risk, tick);
        lastSignalDir = DIR_LONG;
        lastSignalBar = i;

        markers.push({
          time: candles[i].time,
          position: "belowBar",
          color: "#008000",
          shape: "arrowUp",
          text: "",
          size: 2,
        });
        arrowMarkers.push({ time: candles[i].time, direction: "long", price: candles[i].low });
      } else if (isSellSignal) {
        SIGNAL[i] = DIR_SHORT;
        ENTRY[i] = roundToTick(candles[i].close, tick);
        SL[i] = roundToTick(shortTS[i], tick);
        TSL[i] = SL[i];
        const risk = Math.max(candles[i - 1].high - candles[i].close, 0.01);
        TP1[i] = roundToTick(ENTRY[i] - 1.5 * risk, tick);
        TP2[i] = roundToTick(ENTRY[i] - 3.5 * risk, tick);
        lastSignalDir = DIR_SHORT;
        lastSignalBar = i;

        markers.push({
          time: candles[i].time,
          position: "aboveBar",
          color: "#FF0000",
          shape: "arrowDown",
          text: "",
          size: 2,
        });
        arrowMarkers.push({ time: candles[i].time, direction: "short", price: candles[i].high });
      } else {
        SIGNAL[i] = lastSignalDir;
        ENTRY[i] = ENTRY[i - 1] || 0;
        SL[i] = SL[i - 1] || 0;
        TP1[i] = TP1[i - 1] || 0;
        TP2[i] = TP2[i - 1] || 0;
      }

      // Trailing stop tracks SuperTrend band
      if (lastSignalDir === DIR_LONG && longTS[i] > 0) {
        TSL[i] = roundToTick(longTS[i], tick);
      } else if (lastSignalDir === DIR_SHORT && shortTS[i] > 0) {
        TSL[i] = roundToTick(shortTS[i], tick);
      } else {
        TSL[i] = TSL[i - 1] || 0;
      }

      // Candle color state
      if (lastSignalDir === DIR_LONG) {
        candleColorState[i] = "long";
      } else if (lastSignalDir === DIR_SHORT) {
        candleColorState[i] = "short";
      } else {
        candleColorState[i] = "neutral";
      }
    }

    // Forward-fill to forming bar
    if (lastClosedBar >= 0 && lastClosedBar < len - 1) {
      for (let i = lastClosedBar + 1; i < len; i++) {
        SIGNAL[i] = SIGNAL[lastClosedBar];
        ENTRY[i] = ENTRY[lastClosedBar];
        SL[i] = SL[lastClosedBar];
        TSL[i] = TSL[lastClosedBar];
        TP1[i] = TP1[lastClosedBar];
        TP2[i] = TP2[lastClosedBar];
        candleColorState[i] = candleColorState[lastClosedBar];
      }
    }

    // ════════════════════════════════════════════════════════════
    // 3. Target / SL hit detection — chronological bar-by-bar
    //    Priority: targets hit BEFORE stops → book target profit
    // ════════════════════════════════════════════════════════════
    let status: PrimeMagicDashboard["status"] = "Active";
    let tp1Hit = false;
    let tp2Hit = false;
    let slHit = false;
    let tslHit = false;
    let bookedPL = 0;

    if (lastSignalBar >= 0) {
      const entryVal = ENTRY[lastSignalBar];
      const slVal = SL[lastSignalBar];
      const tp1Val = TP1[lastSignalBar];
      const tp2Val = TP2[lastSignalBar];
      const sigDir = SIGNAL[lastSignalBar];

      // Find the FIRST bar each level is breached
      let tp1HitBar = -1, tp2HitBar = -1, slHitBar = -1, tslHitBar = -1;
      let tslHitPrice = 0;

      // Include live bar (len-1) so status updates in real-time on price touch
      for (let i = lastSignalBar + 1; i < len; i++) {
        if (sigDir === DIR_LONG) {
          if (tp1HitBar < 0 && candles[i].high >= tp1Val) tp1HitBar = i;
          if (tp2HitBar < 0 && candles[i].high >= tp2Val) tp2HitBar = i;
          if (slHitBar < 0 && candles[i].low <= slVal) slHitBar = i;
          if (tslHitBar < 0 && candles[i].low <= TSL[i] && TSL[i] > slVal) { tslHitBar = i; tslHitPrice = TSL[i]; }
        } else {
          if (tp1HitBar < 0 && candles[i].low <= tp1Val) tp1HitBar = i;
          if (tp2HitBar < 0 && candles[i].low <= tp2Val) tp2HitBar = i;
          if (slHitBar < 0 && candles[i].high >= slVal) slHitBar = i;
          if (tslHitBar < 0 && candles[i].high >= TSL[i] && TSL[i] < slVal) { tslHitBar = i; tslHitPrice = TSL[i]; }
        }
      }

      tp1Hit = tp1HitBar >= 0;
      tp2Hit = tp2HitBar >= 0;
      slHit = slHitBar >= 0;
      tslHit = tslHitBar >= 0;

      // P&L sign: LONG profits when price rises, SHORT when price falls
      const plSign = sigDir === DIR_LONG ? 1 : -1;

      // Priority rules:
      //  • Hard SL hit before a target → SL wins (clear loss, no target reached)
      //  • Target hit at any bar (even if TSL also fired) → target wins
      //    (TSL tracks the SuperTrend band and can fire on minor dips mid-trend;
      //     if price reached the target the booked result is the target profit)
      //  • TSL determines status/P&L only when no target was achieved at all
      const hardSLBar = slHit ? slHitBar : Infinity;

      if (tp2Hit && tp2HitBar <= hardSLBar) {
        status = "Target2 Hit";
        tp1Hit = true;
        bookedPL = plSign * (tp2Val - entryVal);
      } else if (tp1Hit && tp1HitBar <= hardSLBar) {
        status = "Target1 Hit";
        bookedPL = plSign * (tp1Val - entryVal);
      } else if (slHit && (!tslHit || slHitBar <= tslHitBar)) {
        status = "Stop Loss Hit";
        bookedPL = plSign * (slVal - entryVal);
      } else if (tslHit) {
        status = "Trailing SL Hit";
        bookedPL = plSign * (tslHitPrice - entryVal);
      }
    }

    // ════════════════════════════════════════════════════════════
    // 4. Dashboard metadata
    // ════════════════════════════════════════════════════════════
    const uiBar = Math.max(0, len - 2);
    const currentPrice = candles[len - 1]?.close ?? 0;
    const currSignal = SIGNAL[uiBar];
    const currEntry = ENTRY[uiBar];
    const currSL = SL[uiBar];
    const currTSL = TSL[uiBar];
    const currTP1 = TP1[uiBar];
    const currTP2 = TP2[uiBar];

    let dashSignal: PrimeMagicDashboard["signal"] = null;
    let dashTrend: PrimeMagicDashboard["trend"] = "NEUTRAL";
    let livePL = 0;

    if (currSignal !== DIR_NONE && currEntry > 0) {
      if (currSignal === DIR_LONG) {
        dashSignal = "BUY"; dashTrend = "LONG"; livePL = currentPrice - currEntry;
      } else {
        dashSignal = "SELL"; dashTrend = "SHORT"; livePL = currEntry - currentPrice;
      }
    }

    const dashboard: PrimeMagicDashboard = {
      signal: dashSignal, trend: dashTrend,
      entry: currEntry > 0 ? currEntry : null,
      stopLoss: currSL > 0 ? currSL : null,
      trailingStop: currTSL > 0 ? currTSL : null,
      target1: currTP1 > 0 ? currTP1 : null,
      target2: currTP2 > 0 ? currTP2 : null,
      currentPrice, livePL, bookedPL, status,
      tp1Hit, tp2Hit, slHit, tslHit,
      candleState: candleColorState[uiBar] || "neutral",
    };

    // ════════════════════════════════════════════════════════════
    // 5. Build plot data + candle colors + hit markers
    // ════════════════════════════════════════════════════════════
    const entryData: { time: number; value: number }[] = [];
    const slData: { time: number; value: number }[] = [];
    const t1Data: { time: number; value: number }[] = [];
    const t2Data: { time: number; value: number }[] = [];

    // Plot series intentionally left empty — priceLines handle all level drawing.

    // MT4 candle colors: Green for long, Red for short, Gray for neutral
    const candleColors: { time: number; color: string }[] = [];
    if (showBackground !== "no") for (let i = 0; i < len; i++) {
      const state = candleColorState[i];
      if (state === "long") {
        candleColors.push({ time: candles[i].time, color: "#008000" });
      } else if (state === "short") {
        candleColors.push({ time: candles[i].time, color: "#FF0000" });
      } else {
        candleColors.push({ time: candles[i].time, color: "#808080" });
      }
    }

    // Hit markers on the actual bars where targets/SL were reached
    if (lastSignalBar >= 0 && (tp1Hit || tp2Hit || slHit)) {
      const sigDir = SIGNAL[lastSignalBar];
      const tp1V = TP1[lastSignalBar], tp2V = TP2[lastSignalBar], slV = SL[lastSignalBar];
      let _tp1 = tp1Hit, _tp2 = tp2Hit, _sl = slHit;
      for (let i = lastSignalBar + 1; i <= Math.min(lastClosedBar, len - 1); i++) {
        if (sigDir === DIR_LONG) {
          if (_tp1 && candles[i].high >= tp1V) { markers.push({ time: candles[i].time, position: "aboveBar", shape: "circle", color: "#006400", text: "✓ T1", size: 1 }); _tp1 = false; }
          if (_tp2 && candles[i].high >= tp2V) { markers.push({ time: candles[i].time, position: "aboveBar", shape: "circle", color: "#006400", text: "✓ T2", size: 1 }); _tp2 = false; }
          if (_sl && candles[i].low <= slV) { markers.push({ time: candles[i].time, position: "belowBar", shape: "circle", color: "#FF0000", text: "✗ SL", size: 1 }); _sl = false; }
        } else {
          if (_tp1 && candles[i].low <= tp1V) { markers.push({ time: candles[i].time, position: "belowBar", shape: "circle", color: "#006400", text: "✓ T1", size: 1 }); _tp1 = false; }
          if (_tp2 && candles[i].low <= tp2V) { markers.push({ time: candles[i].time, position: "belowBar", shape: "circle", color: "#006400", text: "✓ T2", size: 1 }); _tp2 = false; }
          if (_sl && candles[i].high >= slV) { markers.push({ time: candles[i].time, position: "aboveBar", shape: "circle", color: "#FF0000", text: "✗ SL", size: 1 }); _sl = false; }
        }
      }
    }

    // ── Price lines for full-width horizontal levels with badge labels ──
    const priceLines: IndicatorPriceLine[] = [];
    if (displayLevels !== "no" && currEntry > 0 && lastSignalBar >= 0) {
      const entryTime = candles[lastSignalBar].time;
      priceLines.push(
        { id: "pmg-entry", price: currEntry, color: "#1565C0", title: "Entry",     lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        { id: "pmg-sl",    price: currSL,    color: "#C62828", title: "Stop Loss", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        { id: "pmg-t1",   price: currTP1,   color: "#2E7D32", title: "Target_1",  lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        { id: "pmg-t2",   price: currTP2,   color: "#2E7D32", title: "Target_2",  lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
      );
      if (showTrailingStop !== "no" && currTSL > 0) {
        priceLines.push(
          { id: "pmg-tsl", price: currTSL, color: "#E65100", title: "Trail SL", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        );
      }
    }

    return [
      { key: "entry", data: entryData, markers, priceLines, metadata: { dashboard, candleColors, arrowMarkers } },
      { key: "stopLoss", data: slData },
      { key: "target1", data: t1Data },
      { key: "target2", data: t2Data },
    ];
  },
};
